import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Header,
  Icon,
  Button,
  Message,
  Loader,
} from "semantic-ui-react";

const BulkFacultyPrintModal = ({ open, onClose, facultyData, loading }) => {
  const [barcodesReady, setBarcodesReady] = useState(false);
  const [barcodeImages, setBarcodeImages] = useState({});

  // Generate barcode as base64 image using canvas
  const generateBarcodeImage = useCallback((libraryId) => {
    return new Promise((resolve, reject) => {
      try {
        if (!window.JsBarcode) {
          reject(new Error("JsBarcode not loaded"));
          return;
        }

        // Create a temporary canvas
        const canvas = document.createElement("canvas");
        const cleanId = libraryId.replace(/[^\x20-\x7E]/g, "");

        // Generate barcode on canvas
        window.JsBarcode(canvas, cleanId, {
          format: "CODE128",
          width: 2,
          height: 60,
          displayValue: false,
          margin: 10,
          background: "#ffffff",
          lineColor: "#000000",
        });

        // Convert canvas to base64 image
        const dataURL = canvas.toDataURL("image/png");
        resolve(dataURL);
      } catch (error) {
        reject(error);
      }
    });
  }, []);

  // Print function using only separate print window
  const handlePrint = () => {
    if (!barcodesReady) {
      alert("Barcodes are still loading. Please wait a moment and try again.");
      return;
    }

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Please allow popups to print the barcodes");
      return;
    }

    // Filter faculty with library IDs
    const facultyWithIds = facultyData.filter((faculty) => faculty.library_id);

    if (facultyWithIds.length === 0) {
      alert("No faculty members with library IDs found to print.");
      printWindow.close();
      return;
    }

    // Generate minimal HTML for faculty barcodes - just ID and barcode
    const facultyCardsHtml = facultyWithIds
      .map((faculty) => {
        const barcodeImage = barcodeImages[faculty.library_id];
        return `
      <div class="barcode-item">
        <div class="faculty-id">Faculty ID: ${faculty.library_id}</div>
        ${
          barcodeImage
            ? `<img src="${barcodeImage}" alt="Barcode for ${faculty.library_id}">`
            : `<div class="no-barcode">Barcode not available</div>`
        }
      </div>
    `;
      })
      .join("");

    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Faculty Barcodes</title>
        <style>
          @page {
            margin: 0.25in;
            size: A4;
          }
          body {
            margin: 0;
            padding: 10px;
            font-family: Arial, sans-serif;
            background: white;
            color: black;
          }
          .barcode-grid {
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            gap: 1px;
            width: 100%;
          }
          .barcode-item {
            text-align: center;
            page-break-inside: avoid;
            margin-bottom: 15px;
            padding: 15px;
            background: #fff;
          }
          .faculty-id {
            font-size: 10px;
            font-weight: bold;
            margin-bottom: 8px;
            color: black;
          }
          .barcode-item img {
            display: block;
            margin: 0 auto;
            max-width: 100%;
            height: auto;
            border: 2px dashed #333;
            padding: 5px;
            border-radius: 4px;
          }
          .no-barcode {
            padding: 10px;
            background: #f0f0f0;
            color: #666;
            font-size: 12px;
          }
          @media print {
            body {
              margin: 0;
              padding: 10px;
            }
            .barcode-item {
              page-break-inside: avoid;
              padding: 15px !important;
            }
            .barcode-item img {
              max-width: 100% !important;
              height: auto !important;
              border: 2px dashed #333 !important;
              padding: 5px !important;
              border-radius: 4px !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="barcode-grid">
          ${facultyCardsHtml}
        </div>
        
        <script>
          setTimeout(function() {
            window.focus();
            window.print();
          }, 500);
        </script>
      </body>
    </html>
  `);

    printWindow.document.close();
  };

  // Use JsBarcode for preview (keeping the preview functionality)
  const renderJsBarcode = (libraryId, containerId) => {
    if (!libraryId) return null;

    return (
      <div
        style={{
          textAlign: "center",
          padding: "1rem",
          border: "2px solid #f4b952",
          borderRadius: "8px",
          backgroundColor: "#fff",
        }}
      >
        <svg
          id={containerId}
          style={{
            marginBottom: "0.5rem",
            display: "block",
            margin: "0 auto",
          }}
        />
      </div>
    );
  };

  // Generate barcode images when modal opens
  useEffect(() => {
    if (open && facultyData.length > 0) {
      setBarcodesReady(false);
      setBarcodeImages({});

      const generateAllBarcodes = async () => {
        if (!window.JsBarcode) {
          setTimeout(generateAllBarcodes, 200);
          return;
        }

        const images = {};
        const facultyWithIds = facultyData.filter(
          (faculty) => faculty.library_id
        );

        // Generate barcode images for print
        for (const faculty of facultyWithIds) {
          const imageData = await generateBarcodeImage(
            faculty.library_id
          ).catch(() => null);
          if (imageData) {
            images[faculty.library_id] = imageData;
          }
        }

        setBarcodeImages(images);

        // Generate SVG barcodes for preview
        setTimeout(() => {
          facultyData.forEach((faculty, index) => {
            if (faculty.library_id && window.JsBarcode) {
              const containerId = `barcode-${faculty.id}-${index}`;
              const element = document.getElementById(containerId);
              if (element) {
                const cleanLibraryId = faculty.library_id.replace(
                  /[^\x20-\x7E]/g,
                  ""
                );
                window.JsBarcode(`#${containerId}`, cleanLibraryId, {
                  format: "CODE128",
                  width: 2,
                  height: 60,
                  displayValue: false,
                  margin: 10,
                  background: "#ffffff",
                  lineColor: "#000000",
                });
              }
            }
          });

          setBarcodesReady(true);
        }, 500);
      };

      generateAllBarcodes();
    }
  }, [open, facultyData, generateBarcodeImage]);

  const renderFacultyCard = (faculty, index) => (
    <div
      key={faculty.id}
      style={{
        pageBreakInside: "avoid",
        marginBottom: "2rem",
        padding: "1rem",
        border: "1px solid #ccc",
        borderRadius: "8px",
        width: "100%",
        backgroundColor: "#fff",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: "1rem" }}>
        <Header as="h4" style={{ margin: "0 0 0.5rem 0", color: "#225745" }}>
          {faculty.full_name || faculty.name}
        </Header>
        <small style={{ color: "#666" }}>
          Faculty ID: {faculty.library_id || "Not assigned"}
        </small>
      </div>

      {faculty.library_id ? (
        renderJsBarcode(faculty.library_id, `barcode-${faculty.id}-${index}`)
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeaa7",
            borderRadius: "4px",
            color: "#856404",
          }}
        >
          <Icon name="warning sign" size="large" />
          <div style={{ marginTop: "0.5rem" }}>
            <strong>No Library ID Set</strong>
            <br />
            <small>
              This faculty member needs to set their library ID to generate a
              barcode.
            </small>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header>
        <Icon name="print" color="purple" />
        Bulk Print Faculty Library IDs
      </Modal.Header>

      <Modal.Content>
        {loading ? (
          <div style={{ textAlign: "center", padding: "3rem" }}>
            <Loader active inline />
            <p style={{ marginTop: "1rem" }}>Loading faculty data...</p>
          </div>
        ) : facultyData.length === 0 ? (
          <Message warning>
            <Message.Header>No Faculty Library IDs Found</Message.Header>
            <p>No faculty members with library IDs were found to print.</p>
          </Message>
        ) : (
          <>
            {/* Load JsBarcode if not already loaded */}
            {!window.JsBarcode && (
              <script
                src="https://cdnjs.cloudflare.com/ajax/libs/jsbarcode/3.11.5/JsBarcode.all.min.js"
                onLoad={() => {}}
              />
            )}

            {/* Print controls */}
            <div style={{ marginBottom: "1rem", textAlign: "center" }}>
              <Button
                primary
                icon="print"
                content={
                  barcodesReady
                    ? `Print ${facultyData.length} Faculty Library IDs`
                    : "Generating Barcodes..."
                }
                onClick={handlePrint}
                size="large"
                disabled={!barcodesReady}
                loading={!barcodesReady}
              />
              {barcodesReady && (
                <div
                  style={{
                    marginTop: "0.5rem",
                    color: "green",
                    fontSize: "12px",
                  }}
                >
                  ✓ Barcodes ready for printing
                </div>
              )}
            </div>

            {/* Print-friendly layout */}
            <div
              id="faculty-print-area"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "1rem",
                maxHeight: "60vh",
                overflowY: "auto",
                padding: "1rem",
              }}
            >
              {facultyData.map(renderFacultyCard)}
            </div>
          </>
        )}
      </Modal.Content>
    </Modal>
  );
};

BulkFacultyPrintModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  facultyData: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
};

export default BulkFacultyPrintModal;
